import { ContractTypeGroupEnum } from '@app/modules/contract/models/contract.model';
import { ErsStatusEnum }         from '@app/modules/ers/models/ers.model';
import { Address }               from '@shared/components/address/models/address.model';

// ENUMS #############################################################
// API enums *********************************************************
export enum UsageItemFullTypeEnum {
	ACTIVE_CLOUD = 'activeCloud',
	ACTIVE_CLOUD_FCC = 'activeCloudFcc',
	ACTIVE_CLOUD_FCC_DC = 'activeCloudFccDc',
	ACTIVE_OFFLINE = 'activeOffline',
	ACTIVE_SWISSBIT_TOKEN = 'activeSwissbitToken',
	ACTIVE_TOKEN = 'activeToken',
	ACTIVATION_CLOUD = 'activationCloud',
	ACTIVATION_CLOUD_FCC = 'activationCloudFcc',
	ACTIVATION_CLOUD_FCC_DC = 'activationCloudFccDc',
	ACTIVATION_OFFLINE = 'activationOffline',
	ACTIVATION_SWISSBIT_TOKEN = 'activationSwissbitToken',
	ACTIVATION_TOKEN = 'activationToken',
	EXT_ERS = 'extErs',
	RECORDED_CLOUD = 'recordedCloud',
	RECORDED_CLOUD_FCC = 'recordedCloudFcc',
	RECORDED_CLOUD_FCC_DC = 'recordedCloudFccDc',
	RECORDED_OFFLINE = 'recordedOffline',
	RECORDED_SWISSBIT_TOKEN = 'recordedSwissbitToken',
	RECORDED_TOKEN = 'recordedToken',
	USAGE_CLOUD = 'usageCloud',
	USAGE_CLOUD_FCC = 'usageCloudFcc',
	USAGE_CLOUD_FCC_DC = 'usageCloudFccDc',
	USAGE_OFFLINE = 'usageOffline',
	USAGE_SWISSBIT_TOKEN = 'usageSwissbitToken',
	USAGE_TOKEN = 'usageToken',
	VOLUME_CLOUD = 'volumeCloud',
	VOLUME_ACTIVE_BUSINESS_PREMISES = 'volumeActiveBusinessPremises',
	VOLUME_DEVIATION = 'volumeDeviation',
}

// UI enums **********************************************************
export enum UsageContractTypeGroupVariantEnum {
	FCC_DATA_CENTER_CONTRACT_TYPE_GROUP = 'fccDataCenterContractTypeGroup',
	CONTRACT_TYPE_GROUP = 'contractTypeGroup'
}

export enum UsageTssTypeEnum {
	CLOUD = 'cloud',
	CLOUD_FCC_DC = 'cloudFccDc',
	EXT_ERS = 'extErs',
	OFFLINE = 'offline',
	SWISSBIT_TOKEN = 'swissbitToken',
	TOKEN = 'token'
}

export enum ContractTypeGroupUsageItemTypeEnum {
	ACTIVE = 'active',
	ACTIVATION = 'activation',
	RECORDED = 'recorded',
	USAGE = 'usage'
}

export enum OtherUsageItemTypeEnum {
	TOTAL = 'total'
}

// INTERFACES ########################################################
// API interfaces ****************************************************

// UI interfaces *****************************************************
export interface IUsageItem {
	count: number;
	type: UsageItemType;
}

export interface IVolumeData {
	businessPremises: {
		activeBusinessPremises: number;
		minBusinessPremises: number;
		maxBusinessPremises: number;
		signaturesPoolContributionPerBusinessPremises: number;
	};
	signatures: {
		maxSignaturesPoolCount: number;
		signaturesCountOfMonth: number;
	};
}

// TYPES #############################################################
export type FiskalEntityUsageType =
	BusinessPremisesUsage |
	ContractUsage |
	OrganizationUsage |
	TenantUsage;

export type UsageItemType =
	ContractTypeGroupUsageItemTypeEnum |
	OtherUsageItemTypeEnum;

export type UsageTssTypeToContractTypeGroupMap = Map<UsageTssTypeEnum, ContractTypeGroupEnum>

// ENTITIES ##########################################################
// MODEL USED FOR TRANSITION APP<->API (TransitionalModelObject)
export class UsageTMO {
	data?: Array<UsageItem> = [];
	periodEnd?: Date = new Date();
	periodStart?: Date = new Date();
}

export class UsageItemTMO {
	count?: number = undefined;
	type?: UsageItemFullTypeEnum = undefined;
}

export class UsageBaseTMO {
	businessPremisesLimit?: number = undefined;
	contractedBusinessPremises?: number = undefined;
	contractedCloudERS?: number = undefined;
	contractedFccDataCenterErs?: number = undefined;
	contractedTokenERS?: number = undefined;
	contractTypeGroup?: ContractTypeGroupEnum = undefined;
	contractId?: string = undefined;
	fccDataCenterContractTypeGroup?: ContractTypeGroupEnum = undefined;
	id?: string = undefined;
	projectNumber?: string = undefined;
	volume?: number = undefined;
}

export class BusinessPremisesUsageTMO extends UsageBaseTMO {
	activeFrom?: Date = undefined;
	activeTo?: Date = undefined;
	addressData?: Address = new Address();
	businessPremisesIdentifier?: string = undefined;
	erses?: ErsUsage[] = [];
	name?: string = undefined;
	notes?: string = undefined;
	usage?: Usage = new Usage();
	virtualTsses?: TssUsage[] = [];
}

export class CustomerAndContractUsageBaseTMO extends UsageBaseTMO {
	customerIdentifier?: string = undefined;
	parentTenantId?: string = undefined;
	usage?: Usage = new Usage();
}

export class ContractUsageTMO extends CustomerAndContractUsageBaseTMO {}

export class ContractUsageTenantsTMO extends ContractUsageTMO {
	tenants?: TenantUsage[] = undefined;
}

export class OrganizationUsageTMO extends UsageBaseTMO {
	addressData?: Address = new Address();
	businessPremises?: Array<BusinessPremisesUsage> = [];
	name?: string = undefined;
	notes?: string = undefined;
	organizationIdentifier?: string = undefined;
	taxNumber?: string = undefined;
	usage?: Usage = new Usage();
}

export class TenantUsageTMO extends UsageBaseTMO {
	billingAddress?: Address = new Address();
	customerIdentifier?: string = undefined;
	name?: string = undefined;
	notes?: string = undefined;
	organizations?: Array<OrganizationUsage> = [];
	usage?: Usage = new Usage();
}

export class ErsUsageTMO {
	ersIdentifier: string = undefined;
	ersSerialNumber: string = undefined;
	id: string = undefined;
	usage?: ErsUsageItem[] = [];
}

export class ErsUsageItemTMO {
	from: Date = undefined;
	status?: ErsStatusEnum = undefined;
	to?: Date = undefined;
	type: UsageItemFullTypeEnum = undefined;
}

export class TssUsageTMO {
	fccId: string = undefined;
	id: string = undefined;
	usage?: TssUsageItem[] = [];
}

export class TssUsageItemTMO {
	count: number = undefined;
	endCounter?: number = undefined;
	from: Date = undefined;
	startCounter?: number = undefined;
	to?: Date = undefined;
}

// MODEL USED IN APPLICATION (with additional properties)
export class Usage extends UsageTMO {}

export class UsageItem extends UsageItemTMO {}

export class BusinessPremisesUsage extends BusinessPremisesUsageTMO {}

export class ContractUsage extends ContractUsageTenantsTMO {}

export class OrganizationUsage extends OrganizationUsageTMO {}

export class TenantUsage extends TenantUsageTMO {}

export class ErsUsage extends ErsUsageTMO {}

export class ErsUsageItem extends ErsUsageItemTMO {}

export class TssUsage extends TssUsageTMO {}

export class TssUsageItem extends TssUsageItemTMO {}
